/**
 * The assistant window's size and place as pure functions, so they can be
 * tested without a DOM. The window is anchored bottom-right: `right`/`bottom`
 * are its distance from the viewport's bottom-right corner, so its header
 * (top edge) stays reachable as long as `bottom + height <= viewport.height`.
 */
export type PanelGeometry = {
  width: number;
  height: number;
  expanded: boolean;
  right: number;
  bottom: number;
};

export type Viewport = { width: number; height: number };

export const PANEL_MIN = { width: 360, height: 400 };
export const PANEL_MARGIN = 16;

export const DEFAULT_GEOMETRY: PanelGeometry = {
  width: 440,
  height: 600,
  expanded: false,
  right: PANEL_MARGIN,
  bottom: PANEL_MARGIN,
};

const between = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

const validNumber = (value: unknown, min: number, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) && value >= min
    ? value
    : fallback;

/** Turns whatever was stored in the browser into a usable geometry. */
export function sanitizeGeometry(raw: unknown): PanelGeometry {
  if (typeof raw !== "object" || raw === null) return DEFAULT_GEOMETRY;
  const { width, height, expanded, right, bottom } = raw as Record<
    string,
    unknown
  >;
  return {
    width: validNumber(width, PANEL_MIN.width, DEFAULT_GEOMETRY.width),
    height: validNumber(height, PANEL_MIN.height, DEFAULT_GEOMETRY.height),
    expanded: expanded === true,
    right: validNumber(right, 0, DEFAULT_GEOMETRY.right),
    bottom: validNumber(bottom, 0, DEFAULT_GEOMETRY.bottom),
  };
}

/** Keeps the whole window (and so its header) inside the viewport. */
export function clampGeometry(
  geometry: PanelGeometry,
  viewport: Viewport
): PanelGeometry {
  const maxWidth = Math.max(0, viewport.width - 2 * PANEL_MARGIN);
  const maxHeight = Math.max(0, viewport.height - 2 * PANEL_MARGIN);
  const width = between(
    geometry.width,
    Math.min(PANEL_MIN.width, maxWidth),
    maxWidth
  );
  const height = between(
    geometry.height,
    Math.min(PANEL_MIN.height, maxHeight),
    maxHeight
  );
  return {
    ...geometry,
    width,
    height,
    right: between(geometry.right, 0, viewport.width - width),
    bottom: between(geometry.bottom, 0, viewport.height - height),
  };
}

/** `start` is the geometry when the drag began; `dx`/`dy` the pointer travel. */
export function movedGeometry(
  start: PanelGeometry,
  dx: number,
  dy: number,
  viewport: Viewport
): PanelGeometry {
  return clampGeometry(
    { ...start, right: start.right - dx, bottom: start.bottom - dy },
    viewport
  );
}

/**
 * Resizing by the top-left corner: the bottom-right corner stays where it is,
 * and the size is limited by the room left up/left of it, so the corner never
 * has to be shifted to keep the window inside the viewport.
 */
export function resizedGeometry(
  start: PanelGeometry,
  dx: number,
  dy: number,
  viewport: Viewport
): PanelGeometry {
  return clampGeometry(
    {
      ...start,
      expanded: false,
      width: between(
        start.width - dx,
        PANEL_MIN.width,
        viewport.width - start.right
      ),
      height: between(
        start.height - dy,
        PANEL_MIN.height,
        viewport.height - start.bottom
      ),
    },
    viewport
  );
}

/** MUI X Chat's conversation-list sidebar width (its `--ChatBox-conversationListWidth` default). */
const CONVERSATION_LIST_WIDTH = 260;

/**
 * The narrowest panel that shows the conversation history beside the
 * thread: the list plus a thread no narrower than the narrowest panel
 * allowed at all — so answers, forms and their buttons keep the room they
 * are laid out for. Below it, the history is an overlay opened on demand.
 */
export const CONVERSATION_SIDEBAR_MIN_PANEL_WIDTH =
  CONVERSATION_LIST_WIDTH + PANEL_MIN.width;

/**
 * Where the conversation history goes for a panel `panelWidth` wide:
 * beside the thread, or an overlay opened on demand. Side by side until the
 * panel was measured (`null`).
 */
export function conversationListLayout(
  panelWidth: number | null
): "sidebar" | "overlay" {
  return panelWidth !== null &&
    panelWidth < CONVERSATION_SIDEBAR_MIN_PANEL_WIDTH
    ? "overlay"
    : "sidebar";
}
